

import { useContext, useState } from "react";
import { RegisterContext } from "@profile/contexts/RegisterContext";
import { TextField, Button, cn } from "@fmc-digital/ui";
import { useTranslation } from "@fmc-digital/i18n";
import { triggerOtp } from "@fmc-digital/auth-api-generated/src/api/fmc/unauth/customer/triggerOtp";
import type { RegisterTriggerOTPRequest } from "@fmc-digital/auth-api-generated/types/fmc/unauth";
import { registerStepFormStyles } from "@profile/styles/register-flow.styles";
import { RegistrationEvent } from "@profile/features/registration/analytics";
import { useRegistrationProvider } from "@profile/providers/RegistrationProvider";

interface EmailStepProps {
  setStatusMessage: (msg: string | null) => void;
}

export function EmailStep({ setStatusMessage }: EmailStepProps) {
  const { t } = useTranslation();
  const { config, trackRegistrationEvent } = useRegistrationProvider();
  const { email, setEmail, goNext, setOtpId, setOtpFactorId } = useContext(RegisterContext);
  const [touched, setTouched] = useState(false);
  const [loading, setLoading] = useState(false);

  const isValidEmail = (val: string) => {
    if (!val) return false;
    const atIndex = val.indexOf("@");
    if (atIndex < 1 || atIndex !== val.lastIndexOf("@")) return false;
    const dotIndex = val.indexOf(".", atIndex);
    return dotIndex > atIndex + 1 && dotIndex < val.length - 1;
  };
  const showError = touched && !isValidEmail(email);

  const handleNext = async () => {
    setStatusMessage(null);
    if (!isValidEmail(email)) {
      setStatusMessage("register:email_invalid");
      return;
    }
    setLoading(true);
    let prevBaseUrl = undefined;
    try {
      const baseUrlV2 = config.apiBaseUrlV2 || config.apiBaseUrl;
      // Set window.__authShellConfig.apiBaseUrl to baseUrlV2 for this call
      if (typeof window !== 'undefined' && window.__authShellConfig) {
        prevBaseUrl = window.__authShellConfig.apiBaseUrl;
        window.__authShellConfig.apiBaseUrl = baseUrlV2 + "/unauth";
      }
      // Send OTP trigger using registration payload (factorType/email)
      const payload: RegisterTriggerOTPRequest = {
        factor: {
          factorType: "EMAIL",
          email,
        },
      };
      const response = await triggerOtp(payload);
      // API returns: { id, factorId, alreadyValidated }
      if (response && response.id) {
        setOtpId(response.id);
        setOtpFactorId(response.factorId || "");
        try {
          trackRegistrationEvent(RegistrationEvent.EMAIL_OTP_REQUEST_SENT);
        } catch {
          // Analytics must not block navigation
        }
        goNext();
      } else {
        setStatusMessage("register:otp_trigger_failed");
      }
    } catch (err: any) {
      // Try to extract error message from API error
      let msg = "register:otp_trigger_failed";
      if (err?.response?.data?.errorDetails?.message) {
        msg = err.response.data.errorDetails.message;
      } else if (err?.message) {
        msg = err.message;
      }
      setStatusMessage(msg);
    } finally {
      // Restore previous baseUrl
      if (typeof window !== 'undefined' && window.__authShellConfig && prevBaseUrl !== undefined) {
        window.__authShellConfig.apiBaseUrl = prevBaseUrl;
      }
      setLoading(false);
    }
  };

  return (
    <div className={cn(registerStepFormStyles())}>
      <TextField
        value={email}
        onChange={e => {
          setEmail(e.target.value);
          if (!touched) setTouched(true);
        }}
        onBlur={() => setTouched(true)}
        type="email"
        fullWidth
        variant="outlined"
        label="Email"
        placeholder="Email"
        error={showError ? t("auth.error.invalidEmail") : undefined}
        data-testid="email-input"
        disabled={loading}
      />
      <Button
        variant="filled"
        fullWidth
        onClick={handleNext}
        disabled={!email || showError || loading}
        data-testid="next-button"
      >
        {loading ? (
          <span className="flex items-center justify-center gap-2">
            <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
            </svg>
            Loading...
          </span>
        ) : (
          "Next"
        )}
      </Button>
    </div>
  );
}
